export function stripPhone(raw: string): string {
  return (raw || "").replace(/[\s()-]/g, "").trim();
}

export function digitsOnly(raw: string): string {
  return (raw || "").replace(/\D/g, "").trim();
}

/**
 * Normalize a UK phone number into canonical E.164-like form (+44XXXXXXXXXX) when possible.
 * This makes +447... and 07... comparable.
 *
 * Returns null if it can't confidently normalize.
 */
export function normalizeUkPhone(raw: string): string | null {
  const s = stripPhone(raw);
  if (!s) return null;

  let d = digitsOnly(s);
  if (!d) return null;

  // 0044... => 44...
  if (d.startsWith("0044")) d = "44" + d.slice(4);

  // 44XXXXXXXXXX => +44XXXXXXXXXX
  if (d.startsWith("44") && d.length >= 12) {
      return `+44${d.slice(2)}`;
  }

  // 0XXXXXXXXXX => +44XXXXXXXXXX
  if (d.startsWith("0") && d.length >= 11) {
      return `+44${d.slice(1)}`;
  }
  
  // 7XXXXXXXXX (no leading 0) => +447XXXXXXXXX
  if (d.length === 10) {
    return `+44${d}`;
  }

  // If already full international format but no '+' (e.g. from some inputs)
  if (d.length > 10) {
      // Just assume it's roughly correct or un-normalizable
      // We could try to prepend +, but for non-UK that's risky.
  }
  
  // If we can't normalize confidently, return the digits-only version prepended with + if it looks international?
  // Or just return null. Let's return null to avoid bad matches.
  return null;
}

/**
 * Generate reasonable UK phone variants for matching/search:
 * - strips spaces/punctuation
 * - converts between +44XXXXXXXXXX and 0XXXXXXXXXX
 * - handles 0044 prefix
 */
export function ukPhoneVariants(raw: string): string[] {
  const s0 = stripPhone(raw);
  const out = new Set<string>();
  if (!s0) return [];

  out.add(s0);

  const d = digitsOnly(s0);
  if (d) {
    out.add(d);
    // If we have just digits, let's try to generate the other format explicitly
    
    // Case 1: 074... -> +4474...
    if (d.startsWith('0') && d.length >= 11) {
        out.add('44' + d.slice(1));
        out.add('+44' + d.slice(1));
    }
    
    // Case 2: 4474... -> 074...
    if (d.startsWith('44') && d.length >= 12) {
         out.add('0' + d.slice(2));
         out.add('+' + d);
    }
    
    // Case 3: 74... (raw digits, no zero) -> 074..., +4474...
    if (d.length === 10) {
        out.add('0' + d);
        out.add('44' + d);
        out.add('+44' + d);
    }
  }

  // Also keep the +44... -> 0... logic if passed with +
  if (s0.startsWith("+44")) {
    const national = "0" + s0.slice(3);
    out.add(national);
    // Add raw 44...
    out.add(s0.slice(1)); 
  }

  return Array.from(out).filter(Boolean);
}

export function isUkPhoneMatch(candidateRaw: string, variants: string[]): boolean {
  const cand = stripPhone(candidateRaw);
  if (!cand) return false;
  const set = new Set(variants.map(stripPhone));
  return set.has(cand);
}


